import React, { Children } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './Canvas.css';
import { withScrollContext, testable } from '@xingternal/malt-util';
import Portal from '@xingternal/malt-portal';
import { QA_MALT } from '@xingternal/malt-constants';
import CanvasContent from './CanvasContent';
import CanvasClose, {
  CLOSE_COLOR_BLACK,
  CLOSE_COLOR_WHITE,
} from './CanvasClose';

export const BACKGROUND_SEMI_TRANSPARENT_BLACK =
  'BACKGROUND_SEMI_TRANSPARENT_BLACK';
export const BACKGROUND_WHITE = 'BACKGROUND_WHITE';
export const BACKGROUND_TRANSPARENT = 'BACKGROUND_TRANSPARENT';
export const BACKGROUND_BLACK = 'BACKGROUND_BLACK';

const TestableDiv = testable(QA_MALT, 'div');

const styleMap = {
  [BACKGROUND_SEMI_TRANSPARENT_BLACK]: styles.canvasSemiTransparentBlack,
  [BACKGROUND_WHITE]: styles.canvasWhite,
  [BACKGROUND_BLACK]: styles.canvasBlack,
  [BACKGROUND_TRANSPARENT]: '',
};

function isDarkBackground(color) {
  return [BACKGROUND_BLACK, BACKGROUND_SEMI_TRANSPARENT_BLACK].includes(color);
}

function appendCloseProps(hasClose, childrenArray, backgroundStyle) {
  if (!hasClose) {
    return childrenArray;
  }

  return childrenArray.map(child => {
    if (child.type === CanvasClose) {
      return {
        ...child,
        props: {
          color: isDarkBackground(backgroundStyle)
            ? CLOSE_COLOR_WHITE
            : CLOSE_COLOR_BLACK,
          ...child.props,
        },
      };
    } else if (child.type === CanvasContent) {
      return {
        ...child,
        props: {
          ...child.props,
          addTopSpace: true,
        },
      };
    }

    return child;
  });
}

const Canvas = ({
  children,
  className,
  backgroundStyle = BACKGROUND_TRANSPARENT,
  onClick,
  portalContainerId,
  testId,
}) => {
  const fullscreenClassNames = classNames(
    className,
    styles.canvas,
    styleMap[backgroundStyle]
  );

  const childrenArray = Children.toArray(children);
  const hasClose = childrenArray.find(child => child.type === CanvasClose);

  return (
    <Portal containerId={portalContainerId}>
      <TestableDiv
        onClick={onClick}
        className={fullscreenClassNames}
        testId={testId}
      >
        {appendCloseProps(hasClose, childrenArray, backgroundStyle)}
      </TestableDiv>
    </Portal>
  );
};

Canvas.propTypes = {
  /**
   * Style of the canvas.
   *
   * Import these from the package `import { BACKGROUND_WHITE } from '@xingternal/malt-canvas';`
   */
  backgroundStyle: PropTypes.oneOf([
    BACKGROUND_SEMI_TRANSPARENT_BLACK,
    BACKGROUND_WHITE,
    BACKGROUND_BLACK,
    BACKGROUND_TRANSPARENT,
  ]),
  /** Custom click callback that fires when the background is being clicked */
  onClick: PropTypes.func,
  /** Content of the canvas */
  children: PropTypes.node,
  /** Add a custom className to the canvas */
  className: PropTypes.string,
  /** define a testid for the canvas */
  testId: PropTypes.string,
  /** ID of the container the canvas should be rendered into. This falls back to a `div` that is appended to the `body` and removed again as soon as the canvas is closed.  */
  portalContainerId: PropTypes.string,
};

Canvas.displayName = 'Canvas';

export default withScrollContext(Canvas);
